// PocketBase client + typed data access for the Business Card app.
// PocketBase (Go) serves this PWA from pb_public/ and exposes its REST/realtime
// API at the same origin, so the default base URL is '/'. Override with VITE_PB_URL.
import PocketBase from 'pocketbase';
import type { CardTheme } from './card-presets';
import { stylingToCardVars } from './card-vars';
import { saveCollected, listCollected, type PublicProfile, type CardExtras, type Contact, type CardOrg } from './collected';
import { loadMyCard } from './mycard';

// `import.meta.env` is defined by Vite at build time; guard it so the module is
// also importable under the esbuild-based test runner (where it is undefined).
export const pb = new PocketBase(import.meta.env?.VITE_PB_URL || '/');

/** A business card record (collection: `cards`). */
export interface Card {
  id?: string;
  publicId?: string;
  /** Your own private note about a card you COLLECTED. Device-local, never
   *  published, never sent — see lib/collected. Absent on your own cards. */
  note?: string;
  /** Your own labels for a collected card. Device-local, like the note. */
  tags?: string[];
  /** Bearer token that authorises editing this published card in place. Minted
   *  once by publish and kept on this device only — it is what lets the card be
   *  changed without changing its public id, so a programmed NFC tag keeps
   *  working. Never rendered, never sent in a request body. */
  editToken?: string;
  owner?: string;
  name: string;
  title: string;
  /** Which department, on a company card. Arrives only from the org roster
   *  via the public projection; the personal editor never writes it. */
  department?: string;
  company: string;
  monogram: string;
  phone: string;
  email: string;
  website: string;
  address: string;
  /** Additional ways to reach this person. The card FRONT always shows just
   *  the primary phone/email; these appear in the expanded view. */
  extraPhones?: Contact[];
  extraEmails?: Contact[];
  /** Social/profile links keyed by network — the server's closed allow-list
   *  (`allowedSocials` in internal/profile/publish.go). `website` is stored
   *  here too by the server, but the editor keeps it in `website` above; these
   *  render on the card BACK. */
  socials?: Record<string, string>;
  /** Industry slug (platform/industry) — what makes a large collected
   *  directory sortable. Optional; most cards never set it. */
  industry?: string;
  theme: CardTheme;
  styleVars?: string; // AI/custom --card-* override (from the profile's styling)
  styling?: Record<string, string>; // the raw styling object behind styleVars (published as-is)
  photo?: string; // headshot as a data URL (see lib/photo.ts)
  /** A picture replacing the card's designed back (see lib/photo.ts). */
  backImage?: string;
  /** Content behind the front: labelled links and a promo block. Never alters
   *  the front, which stays the standard contact card. */
  extras?: CardExtras;
  /** The company behind this card, if it is an employee card. */
  org?: CardOrg;
  /** Whether someone has proved ownership of this card. A boolean by design —
   *  the projection never says WHO owns it. Undefined for local-only cards. */
  claimed?: boolean;
  /** A company-issued card whose holder has not claimed it yet. The server
   *  sends the id and this flag and NOTHING else, so every other field is
   *  empty — an employer's roster upload must not publish its staff's details
   *  before those people have agreed. Render the waiting state, not a card. */
  pending?: boolean;
  tagline?: string;
  created?: string;
  updated?: string;
}

/** Map the public projection to the display Card shape used by <lead-card>. */
export function projectionToCard(p: PublicProfile): Card {
  return {
    id: p.publicId,
    publicId: p.publicId,
    name: p.name,
    title: p.title || '',
    company: p.company || '',
    monogram: (p.name || '?').trim().charAt(0).toUpperCase() || '?',
    phone: p.phone || '',
    email: p.email || '',
    website: p.socials?.website || '',
    address: p.address || '',
    industry: p.industry || '',
    extraPhones: p.extraPhones,
    extraEmails: p.extraEmails,
    org: p.org,
    // Render the card the way its owner built it; 'graphite' is only the
    // fallback for cards published before the theme was carried across.
    theme: (p.theme as CardTheme) || 'graphite',
    styleVars: stylingToCardVars(p.styling),
    photo: p.photoDataUrl || '',
    backImage: p.backImageDataUrl || '',
    extras: p.extras,
    claimed: p.claimed ?? false,
    pending: p.pending ?? false,
    // Carried across only when this projection came from the offline store,
    // which is the only place a note can exist — a card fetched fresh from the
    // server has none, and must not blank one already held.
    ...('note' in p ? { note: (p as { note?: string }).note } : {}),
    ...('tags' in p ? { tags: (p as { tags?: string[] }).tags } : {}),
  };
}

/** The inverse of {@link projectionToCard}: turn a display Card back into the
 *  projection shape the offline directory stores. Used when collecting a card
 *  that is already on screen, so the tap does not need a second round-trip. */
export function toProjection(c: Card): PublicProfile {
  return {
    publicId: c.publicId || c.id || '',
    name: c.name,
    title: c.title || undefined,
    company: c.company || undefined,
    phone: c.phone || undefined,
    email: c.email || undefined,
    address: c.address || undefined,
    industry: c.industry || undefined,
    extraPhones: c.extraPhones,
    extraEmails: c.extraEmails,
    socials: c.website ? { website: c.website } : undefined,
    theme: c.theme,
    photoDataUrl: c.photo || undefined,
    backImageDataUrl: c.backImage || undefined,
    extras: c.extras,
    claimed: c.claimed,
  };
}

/** The Sharee's own card, or `null` if they haven't created one yet. Reads the
 *  locally-saved card (from the creator flow's Save). A `null` result is what
 *  drives the page straight into the creator flow. */
export async function myCard(): Promise<Card | null> {
  return (await loadMyCard<Card>()) ?? null;
}

/** The Receiver's collected directory — device-local (IndexedDB), newest-first.
 *  Works fully offline; no account, no server round-trip. */
export async function collectedCards(): Promise<Card[]> {
  const cards = await listCollected();
  return cards.map(projectionToCard);
}

/** Resolve a public card by its public id via the projection route
 *  (GET /api/v1/profiles/:publicId). On success it is cached into the Receiver's
 *  offline directory (IndexedDB), so a later airplane-mode visit still shows it. */
/**
 * Current projections for cards already held, in ONE request.
 *
 * Not the single-id route in a loop: that records a TAP per call, and a
 * background refresh is not somebody meeting you. Looping it would invent a tap
 * per collected card per launch and make every holder's stats meaningless — and
 * open one connection per card while doing it.
 *
 * Ids that no longer resolve are simply absent from the reply.
 */
export async function fetchProfiles(ids: string[]): Promise<PublicProfile[]> {
  if (!ids.length) return [];
  try {
    const res = await fetch('/api/v1/profiles/batch', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ ids }),
    });
    const env = (await res.json()) as { status: string; data?: { profiles?: PublicProfile[] } };
    if (!res.ok || env.status !== 'success') return [];
    return env.data?.profiles ?? [];
  } catch {
    return [];
  }
}

export async function publicCard(id: string): Promise<Card | null> {
  try {
    const res = await fetch(`/api/v1/profiles/${encodeURIComponent(id)}`);
    const env = (await res.json()) as { status: string; data?: PublicProfile };
    if (!res.ok || env.status !== 'success' || !env.data) return null;
    await saveCollected(env.data); // cache for offline
    return projectionToCard(env.data);
  } catch {
    return null;
  }
}

/** Public share URL for a card — the shortest PATH-based tag URL, resolved by
 *  the SPA fallback (must match internal/nfcpayload's `https://<domain>/<id>`). */
export const shareUrl = (id: string) => `${location.origin}/${id}`;
